/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
        Module No. 17, SBC, Thejaswini Building
        Phase 1, Technopark, Thiruvananthapuram - 695581, 
        Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation, Trans } from 'react-i18next';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import CustomToast from '../../../widgets/toaster';
import Pagination from '../../../widgets/pagination';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import MLTBioburdenInitiate from './Mltbioburdeninitiate';
import MLTBioburdenView from './MLTBioburdenView';
import { MLTBioBurdenServices } from '../../../service/MLTBioBurden';
import { helperFunctions } from '../../../utils/helper';

export interface BioburdenTest {
    task_id: string;
    id: string;
    productionSchedule: string;
    scheduleLine: string;
    productName: string;
    productCode: string;
    batchNo: string;
    sampleType: string;
    sampleId: string;
    testMethod: 'Plate Count' | 'Filtration';
    assignedTo: string;
    scheduledDate: string;
    initiatedDate?: string;
    status: 'Pending' | 'Initiated' | 'Incubation' | 'CFU Recording' | 'Approval Pending' | 'Completed';
    dueDate: string;
    priority: 'High' | 'Medium' | 'Low';
    area?: string;
}


interface MLTBioburdenListProps {
    onInitiate?: (task?: BioburdenTest) => void;
    onStartTask?: (task: BioburdenTest) => void;
    onViewDetails?: (id: string, task?: BioburdenTest) => void;
}

const MLTBioburdenList: React.FC<MLTBioburdenListProps> = ({
    onInitiate,
    onStartTask,
    onViewDetails
}) => {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState<BioburdenTest[]>([]);
    const [activeTab, setActiveTab] = useState<'pending' | 'all' | 'in_progress' | 'completed'>('pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSampleType, setSelectedSampleType] = useState('All');
    const [selectedMethod, setSelectedMethod] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("");
    const [useEffectRequired, setUseEffectRequired] = useState(false);
    // pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sort, setSort] = useState("id");
    const [order, setOrder] = useState("desc");
    const [fromdate, setFromDate] = useState(
        new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
    );
    const currentDate = new Date().toISOString().split('T')[0];
    const [toDate, setToDate] = useState(currentDate);

    const [isInitiating, setIsInitiating] = useState(false);
    const [viewBioBurden, setViewBioBurden] = useState(false);
    const [MLTBioburdenTestId, setMLTBioburdenTestId] = useState("");
    const [selectedTask, setSelectedTask] = useState<any>();

    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const formatApiDate = (value?: string, withTime = false): string => {
        if (!value) return '';
        const [datePart, timePart = ''] = value.split(' ');
        const [y, m, d] = datePart.split('-');
        if (!y || !m || !d) return value;
        const base = `${d}-${MONTHS[Number(m) - 1]}-${y}`;
        return withTime && timePart ? `${base} ${timePart.slice(0, 5)}` : base;
    };

    // API group_test_status -> UI status
    const mapStatus = (status?: string): BioburdenTest['status'] => {
        switch ((status || '').toLowerCase().trim()) {
            case 'pending': return 'Pending';
            case 'initiated': return 'Initiated';
            case 'incubation': return 'Incubation';
            case 'cfu_recording':
            case 'cfu recording': return 'CFU Recording';
            case 'approval_pending':
            case 'approval pending': return 'Approval Pending';
            case 'completed': return 'Completed';
            default: return 'Pending';
        }
    };

    // API item -> BioburdenTest (mock data format preserved)
    const mapApiToBioburdenTest = (item: any): BioburdenTest => ({
        task_id: item.id,
        id: item.task_id,                                         // "MLT-9"
        productionSchedule: item.production_schedule_id ?? '',    // "119"
        scheduleLine: item.production_schedule_name ?? '',        // "03-07-2025Aseptic Area"
        productName: item.product_name ?? '',
        productCode: '',                                          // not in API yet
        batchNo: '',                                              // not in API yet
        sampleType: '',                                           // not in API yet
        sampleId: '',                                             // not in API yet
        testMethod: 'Plate Count',                                // not in API yet (default)
        assignedTo: item.assignee_operator_name ?? '',
        scheduledDate: formatApiDate(item.schedule_date, true),
        initiatedDate: undefined,                                 // not in API yet
        status: mapStatus(item.group_test_status),
        dueDate: formatApiDate(item.schedule_date),               // no due date in API; using schedule date
        priority: 'Medium',                                       // not in API yet (default)
        area: item.production_schedule_name ?? '',
    });

    //api call for table MLTBioburden task listing 
    const getMLTBioburdenTaskListing = async () => {
        try {
            const response = await MLTBioBurdenServices.getMLTBioBurdenListTask(
                page, pageSize, sort, order, fromdate, toDate, searchTerm
            );

            if (response?.status && response?.statusCode === 200) {
                if (Array.isArray(response.data) && response.data.length > 0) {
                    setTasks(response.data.map(mapApiToBioburdenTest));
                    setTotalPages(response?.totalPages ?? 0);
                    setTotalElements(response?.totalElements ?? 0);
                } else {
                    setTasks([]);
                    setTotalPages(0);
                    setTotalElements(0);
                }
            } else {
                setTasks([]);
                setTotalPages(0);
                setTotalElements(0);
            }
        } catch (error) {
            console.error('MLT Bioburden task listing error', error);
            setTasks([]);
            setTotalPages(0);
            setTotalElements(0);
        }
    };

    //handle table pagination 
    const handlePageChange = (newPage: any, pageSize?: number) => {
        setPage(newPage);
        if (pageSize) {
            setPageSize(pageSize)
        }
        setUseEffectRequired(true)
    };

    // Quick Notification Toast
    const triggerToast = (msg: string) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(""), 4000);
    };

    // Counts for tabs & KPIs
    const pendingCount = useMemo(() => tasks.filter(t => t.status === 'Pending').length, [tasks]);
    const initiatedCount = useMemo(() => tasks.filter(t => t.status === 'Initiated').length, [tasks]);
    const incubationCount = useMemo(() => tasks.filter(t => t.status === 'Incubation').length, [tasks]);
    const cfuCount = useMemo(() => tasks.filter(t => t.status === 'CFU Recording').length, [tasks]);
    const approvalCount = useMemo(() => tasks.filter(t => t.status === 'Approval Pending').length, [tasks]);
    const completedCount = useMemo(() => tasks.filter(t => t.status === 'Completed').length, [tasks]);
    const inProgressCount = initiatedCount + incubationCount + cfuCount;

    // Filtered tasks based on activeTab, search, and dropdowns
    const filteredTasks = useMemo(() => {
        return tasks.filter(task => {
            // Tab filter
            if (activeTab === 'pending' && task.status !== 'Pending') return false;
            if (activeTab === 'in_progress' && !['Initiated', 'Incubation', 'CFU Recording', 'Approval Pending'].includes(task.status)) return false;
            if (activeTab === 'completed' && task.status !== 'Completed') return false;

            // Status dropdown override (if not All)
            if (statusFilter !== 'All' && task.status !== statusFilter) return false;

            // Sample type
            if (selectedSampleType !== 'All' && task.sampleType !== selectedSampleType) return false;

            // Test method
            if (selectedMethod !== 'All' && task.testMethod !== selectedMethod) return false;

            // Search term
            if (searchTerm.trim()) {
                const query = searchTerm.toLowerCase();
                const matchesId = task.id.toLowerCase().includes(query);
                const matchesSchedule = task.productionSchedule.toLowerCase().includes(query);
                const matchesProduct = task.productName.toLowerCase().includes(query) || task.productCode.toLowerCase().includes(query);
                const matchesBatch = task.batchNo.toLowerCase().includes(query);
                const matchesSample = task.sampleId.toLowerCase().includes(query) || task.sampleType.toLowerCase().includes(query);
                return matchesId || matchesSchedule || matchesProduct || matchesBatch || matchesSample;
            }

            return true;
        });
    }, [tasks, activeTab, statusFilter, selectedSampleType, selectedMethod, searchTerm]);

    const getStatusBadge = (status: BioburdenTest['status']) => {
        switch (status) {
            case 'Pending':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                        {t("mltBioburdenList.status.pending")}
                    </span>
                );
            case 'Initiated':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                        {t("mltBioburdenList.status.initiated")}
                    </span>
                );
            case 'Incubation':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        {t("mltBioburdenList.status.incubation")}
                    </span>
                );
            case 'CFU Recording':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                        {t("mltBioburdenList.status.cfuRecording")}
                    </span>
                );
            case 'Approval Pending':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                        {t("mltBioburdenList.status.approvalPending")}
                    </span>
                );
            case 'Completed':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        {t("mltBioburdenList.status.completed")}
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">
                        {status}
                    </span>
                );
        }
    };

    const handleStartInitiating = (taskId: string) => {
        setIsInitiating(true);
        const selectedTask = tasks.find((task: any) => task.id === taskId);
        setSelectedTask(selectedTask)
    };

    useEffect(() => {
        getMLTBioburdenTaskListing();
        setUseEffectRequired(false);
    }, [useEffectRequired, fromdate, toDate, searchTerm])


    return (
        <div>
            <HeaderSection />
            <PageHeader pageHeader={t('mltBioburdenList.header.title')} pageDescription={t('mltBioburdenList.header.subtitle')} />
            <CustomToast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={5000}
                message={toastMessage}
                toastType={toastType}
            />

            {isInitiating && (
                <MLTBioburdenInitiate
                    onBack={() => {setIsInitiating(false) ; setUseEffectRequired(true)}}
                    onViewInitiatedTest={(id) => { setIsInitiating(false); setViewBioBurden(true) ; setMLTBioburdenTestId(id) }}
                    taskData={selectedTask}
                />
            )}

            {viewBioBurden && (
                <MLTBioburdenView onBack={() => setViewBioBurden(false)} testId={MLTBioburdenTestId} />
            )}


            {!isInitiating && !viewBioBurden && (
                <div className="min-h-screen bg-[#F8FAFC] p-8 pb-20 media-prep-scope">

                    {/* KPI Section */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 my-2">
                        {[
                            {
                                label: t("mltBioburdenList.kpi.pendingTasks"),
                                value: pendingCount,
                                sub: t("mltBioburdenList.kpi.actionRequired"),
                                color: 'amber',
                                icon: 'schedule',
                                highlight: true,
                                tab: 'pending' as const
                            },
                            {
                                label: t("mltBioburdenList.kpi.inProgress"),
                                value: inProgressCount,
                                sub: t("mltBioburdenList.kpi.inIncubation", { incubationCount }),
                                color: 'blue',
                                icon: 'hourglass_top',
                                tab: 'in_progress' as const
                            },
                            {
                                label: t("mltBioburdenList.kpi.cfuRecording"),
                                value: cfuCount,
                                sub: t("mltBioburdenList.kpi.awaitingColonyCounts"),
                                color: 'indigo',
                                icon: 'numbers'
                            },
                            {
                                label: t("mltBioburdenList.kpi.approvalPending"),
                                value: approvalCount,
                                sub: t("mltBioburdenList.kpi.waitingQaReview"),
                                color: 'rose',
                                icon: 'verified'
                            },
                            {
                                label: t("mltBioburdenList.kpi.completedTests"),
                                value: completedCount,
                                sub: t("mltBioburdenList.kpi.releasedArchived"),
                                color: 'emerald',
                                icon: 'check_circle',
                                tab: 'completed' as const
                            },
                        ].map((kpi, idx) => (
                            <div
                                key={idx}
                                onClick={() => kpi.tab && setActiveTab(kpi.tab)}
                                className={`bg-white p-4 rounded-xl border-slate-200 border-t-4 shadow-sm relative overflow-hidden transition-all ${kpi.tab ? 'cursor-pointer hover:border-slate-300 hover:shadow-md' : ''
                                    } ${kpi.color === 'amber' ? 'border-t-amber-500' :
                                        kpi.color === 'blue' ? 'border-t-blue-500' :
                                            kpi.color === 'indigo' ? 'border-t-indigo-500' :
                                                kpi.color === 'rose' ? 'border-t-rose-500' : 'border-t-emerald-500'
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-1.5">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{kpi.label}</p>
                                    <span className={`material-symbols-outlined text-lg ${kpi.color === 'amber' ? 'text-amber-500' :
                                        kpi.color === 'blue' ? 'text-blue-500' :
                                            kpi.color === 'indigo' ? 'text-indigo-500' :
                                                kpi.color === 'rose' ? 'text-rose-500' : 'text-emerald-500'
                                        }`}>
                                        {kpi.icon}
                                    </span>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <h2 className="text-2xl font-black text-slate-800">{kpi.value}</h2>
                                    {kpi.highlight && (
                                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase tracking-wider">
                                            {t("mltBioburdenList.kpi.readyToStart")}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-1">{kpi.sub}</p>
                            </div>
                        ))}
                    </div>

                    {/* Main Table Card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden my-3">
                        {/* Navigation Tabs Header */}
                        <div className="p-4 border-b border-slate-200 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-2 overflow-x-auto">
                                <button
                                    onClick={() => setActiveTab('pending')}
                                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab === 'pending'
                                        ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                                        : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-sm">pending_actions</span>
                                    {t("mltBioburdenList.tabs.pending")}
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${activeTab === 'pending' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800'
                                        }`}>
                                        {pendingCount}
                                    </span>
                                </button>

                                <button
                                    onClick={() => setActiveTab('all')}
                                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab === 'all'
                                        ? 'bg-[#0F2942] text-white shadow-md'
                                        : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                >
                                    {t("mltBioburdenList.tabs.all")}
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${activeTab === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                                        }`}>
                                        {tasks.length}
                                    </span>
                                </button>

                                <button
                                    onClick={() => setActiveTab('in_progress')}
                                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab === 'in_progress'
                                        ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                                        : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                >
                                    {t("mltBioburdenList.tabs.inProgress")}
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${activeTab === 'in_progress' ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                        {inProgressCount}
                                    </span>
                                </button>

                                <button
                                    onClick={() => setActiveTab('completed')}
                                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab === 'completed'
                                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                                        : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                >
                                    {t("mltBioburdenList.tabs.completed")}
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${activeTab === 'completed' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-800'
                                        }`}>
                                        {completedCount}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Filter Bar */}
                        <div className="p-4 bg-slate-50/80 border-b border-slate-200 d-flex align-items-end gap-3">

                            {/* Search: first half */}
                            <div className="relative" style={{ flex: '0 0 50%', maxWidth: '50%' }}>
                                <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t("mltBioburdenList.filters.searchPlaceholder")}
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500 transition-all placeholder:text-slate-400"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                                    >
                                        <span className="material-symbols-outlined text-sm">close</span>
                                    </button>
                                )}
                            </div>

                            {/* Filters: second half, aligned to the end */}
                            <div
                                className="d-flex justify-content-end gap-3"
                                style={{ flex: '1 1 50%' }}
                            >
                                <div className="d-flex justify-content-end gap-3">

                                    {/* <div className="space-y-1.5">
                                        <label className="filterLabelLeft">{t("mltBioburdenList.filters.status")}</label>
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="areaTypeSelect"
                                        >
                                            <option value="">{t("mltBioburdenList.filters.allStatuses")}</option>
                                            <option value="Pending">{t("mltBioburdenList.status.pending")}</option>
                                            <option value="Initiated">{t("mltBioburdenList.status.initiated")}</option>
                                            <option value="Incubation">{t("mltBioburdenList.status.incubation")}</option>
                                            <option value="CFU Recording">{t("mltBioburdenList.status.cfuRecording")}</option>
                                            <option value="Approval Pending">{t("mltBioburdenList.status.approvalPending")}</option>
                                            <option value="Completed">{t("mltBioburdenList.status.completed")}</option>
                                        </select>
                                    </div> */}

                                    {/* <div className="space-y-1.5">
                                        <label className="filterLabelLeft">{t("mltBioburdenList.filters.sample")}</label>
                                        <select
                                            value={selectedSampleType}
                                            onChange={(e) => setSelectedSampleType(e.target.value)}
                                            className="areaTypeSelect"
                                        >
                                            <option value="All">{t("mltBioburdenList.filters.allSampleTypes")}</option>
                                            <option value="In-Process Bulk">{t("mltBioburdenList.filters.inProcessBulk")}</option>
                                            <option value="Bulk Solution">{t("mltBioburdenList.filters.bulkSolution")}</option>
                                            <option value="Purified Water">{t("mltBioburdenList.filters.purifiedWater")}</option>
                                            <option value="Raw Material">{t("mltBioburdenList.filters.rawMaterial")}</option>
                                            <option value="Finished Product">{t("mltBioburdenList.filters.finishedProduct")}</option>
                                            <option value="Environmental">{t("mltBioburdenList.filters.environmental")}</option>
                                        </select>
                                    </div> */}

                                    {/* <div className="space-y-1.5">
                                        <label className="filterLabelLeft">{t("mltBioburdenList.filters.method")}</label>
                                        <select
                                            value={selectedMethod}
                                            onChange={(e) => setSelectedMethod(e.target.value)}
                                            className="areaTypeSelect"
                                        >
                                            <option value="All">{t("mltBioburdenList.filters.allMethods")}</option>
                                            <option value="Filtration">{t("mltBioburdenList.filters.filtration")}</option>
                                            <option value="Plate Count">{t("mltBioburdenList.filters.plateCount")}</option>
                                        </select>
                                    </div> */}

                                    <div className="space-y-1.5">
                                        <label className="filterLabelLeft">From Date</label>
                                        <input
                                            type="date"
                                            value={fromdate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                            className="form-control mt-2"
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="filterLabelLeft">To Date</label>
                                        <input
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                            className="form-control mt-2"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabular Section */}
                        <div className="overflow-x-auto my-4 h-[60vh]">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="tableHeadRow text-center">
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.taskId")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.productionSchedule")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.product")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.area/location")}</th>
                                        {/* <th className="tableHeadData2">{t("mltBioburdenList.table.batchNo")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.sampleType")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.sampleId")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.testMethod")}</th> */}
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.assignedTo")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.scheduledDate")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.status")}</th>
                                        <th className="tableHeadData2">{t("mltBioburdenList.table.action")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-xs">
                                    {filteredTasks.length === 0 ? (
                                        <tr>
                                            <td colSpan={11} className="px-6 py-12 text-center text-slate-400">
                                                <div className="flex flex-col items-center justify-center gap-2">
                                                    <span className="material-symbols-outlined text-4xl text-slate-300">search_off</span>
                                                    <p className="text-sm font-bold text-slate-600">{t("mltBioburdenList.table.emptyTitle")}</p>
                                                    <p className="text-xs text-slate-400">{t("mltBioburdenList.table.emptyHint")}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTasks.map((task) => {
                                            const isPending = task.status === 'Pending';
                                            return (
                                                <tr
                                                    key={task.id}
                                                    className={`hover:bg-slate-50/70 transition-colors group ${isPending ? 'bg-amber-50/15' : ''
                                                        }`}
                                                >
                                                    {/* Task ID */}
                                                    <td className="px-5 py-4 whitespace-nowrap">
                                                        <button
                                                            onClick={() => {setViewBioBurden(true); setMLTBioburdenTestId(task.id)}}
                                                            className="font-black text-slate-800 hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                                                            title={t("mltBioburdenList.table.clickToView")}
                                                        >
                                                            <span className="px-2.5 py-1 bg-slate-100 group-hover:bg-blue-50 group-hover:text-blue-700 rounded-md text-xs font-mono font-bold tracking-tight border border-slate-200">
                                                                {task.id}
                                                            </span>
                                                        </button>
                                                    </td>

                                                    {/* Production Schedule */}
                                                    <td className="px-5 py-4 whitespace-nowrap bg-blue-50/20 border-x border-slate-100">
                                                        <div className="flex flex-col">
                                                            <span className="font-black text-blue-700 tracking-tight flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-[13px] text-blue-500">event_note</span>
                                                                {task.productionSchedule}
                                                            </span>
                                                            {/* <span className="text-[10px] text-slate-500 font-medium tracking-tight">
                                                            {task.scheduleLine}
                                                        </span> */}
                                                        </div>
                                                    </td>

                                                    {/* Product */}
                                                    <td className="px-5 py-4 whitespace-nowrap bg-indigo-50/15 border-r border-slate-100">
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-slate-900 tracking-tight">
                                                                {task.productName}
                                                            </span>
                                                            <span className="text-[10px] font-mono text-indigo-600 font-bold">
                                                                {task.productCode}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {/* Area / location */}
                                                    <td className="px-5 py-4 whitespace-nowrap">
                                                        <span className="font-mono font-bold text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200/60 text-[11px]">
                                                            {task.scheduleLine}
                                                        </span>
                                                    </td>

                                                    {/* Batch No */}
                                                    {/* <td className="px-5 py-4 whitespace-nowrap">
                                                        <span className="font-mono font-bold text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200/60 text-[11px]">
                                                            {task.batchNo}
                                                        </span>
                                                    </td> */}

                                                    {/* Sample Type */}
                                                    {/* <td className="px-5 py-4 whitespace-nowrap">
                                                        <span className="font-semibold text-slate-600">
                                                            {task.sampleType}
                                                        </span>
                                                    </td> */}

                                                    {/* Sample ID */}
                                                    {/* <td className="px-5 py-4 whitespace-nowrap">
                                                        <span className="font-mono text-slate-500 font-bold text-[11px]">
                                                            {task.sampleId}
                                                        </span>
                                                    </td> */}

                                                    {/* Test Method */}
                                                    {/* <td className="px-5 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider ${task.testMethod === 'Filtration'
                                                            ? 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                                                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                            }`}>
                                                            <span className="material-symbols-outlined text-[11px]">
                                                                {task.testMethod === 'Filtration' ? 'filter_alt' : 'grid_view'}
                                                            </span>
                                                            {task.testMethod}
                                                        </span>
                                                    </td> */}

                                                    {/* Assigned To */}
                                                    <td className="px-5 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5 text-slate-700 font-medium">
                                                            <span className="material-symbols-outlined text-slate-400 text-sm">person</span>
                                                            <span>{task.assignedTo}</span>
                                                        </div>
                                                    </td>

                                                    {/* Scheduled Date */}
                                                    <td className="px-5 py-4 whitespace-nowrap">
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-slate-700 text-[11px]">
                                                                {task.scheduledDate}
                                                            </span>
                                                            <span className="text-[9px] text-slate-400 uppercase font-black tracking-widest">
                                                                {t("mltBioburdenList.table.due", { date: task.dueDate })}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {/* Status */}
                                                    <td className="px-5 py-4 whitespace-nowrap text-center">
                                                        {getStatusBadge(task.status)}
                                                    </td>

                                                    {/* Action: Prominent Start Button for Pending */}
                                                    <td className="px-5 py-4 whitespace-nowrap text-center">
                                                        <div className="flex items-center justify-center gap-1.5">
                                                            {isPending ? (
                                                                <button
                                                                    onClick={() => handleStartInitiating(task.id)}
                                                                    className="px-3.5 py-1.5 bg-[#FF7043] hover:bg-[#F4511E] text-white rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-sm shadow-orange-500/20 active:scale-95 transition-all group-hover:ring-2 ring-orange-200"
                                                                    title={t("mltBioburdenList.table.startTest", { id: task.id })}
                                                                >
                                                                    <span className="material-symbols-outlined text-[15px] font-black">play_arrow</span>
                                                                    <span>{t("mltBioburdenList.table.start")}</span>
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => {setViewBioBurden(true); setMLTBioburdenTestId(task.id)}}
                                                                    className="px-3 py-1.5 bg-[#0F2942] hover:bg-[#1E3A5F] text-white rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm"
                                                                >
                                                                    <span className="material-symbols-outlined text-[14px]">visibility</span>
                                                                    <span>{t("mltBioburdenList.table.view")}</span>
                                                                </button>
                                                            )}

                                                            {isPending && (
                                                                <button
                                                                    onClick={() => {setViewBioBurden(true); setMLTBioburdenTestId(task.id)}}
                                                                    className="p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded-lg transition-all"
                                                                    title={t("mltBioburdenList.table.viewTaskDetails")}
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">visibility</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                        />

                    </div>
                </div>
            )}
        </div>
    );
};

export default MLTBioburdenList;